import { useMemo, type VoidFunctionComponent } from 'react';
import type { RouteComponent } from 'react-router';

import { FULL_SECURITY } from '../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../helpers/features';
import { stripLastPathSegment } from '../../helpers/paths';
import { Divider } from '../generic/Divider';
import Redirect from '../generic/Redirect';
import SidebarNavLayout from '../layouts/SidebarNavLayout';
import { useCanDisplaySecurityOptions } from '../projects/generic/hooks';

import { Blurb } from './components/Blurb';
import { FormatSelector } from './components/FormatSelector';
import { GenerateReportButtonContainer as GenerateReportButton } from './components/GenerateReportButton';
import { Heading } from './components/Heading';
import { ReportFlowSelector } from './components/ReportFlowSelector';
import { StepHeading } from './components/StepHeading';
import { useIsRemediationEnabled } from './hooks/useIsRemediationEnabled';
import { useReportType } from './hooks/useReportType';
import PreviewFiltersLayout from './layout/PreviewFiltersLayout';
import { Preview } from './preview';
import type { ReportType } from './types';

/**
 * Container component for the new Reports "V3"/Reports RewRite flow.
 *
 * This features an updated workflow for generating reports, allowing users
 * to either generate a "standard" report (based on what FOSSA recommends for them)
 * or a "custom" report (based on their own preferences).
 *
 * This component handles scope (ie: "is this a project or release group"),
 * and acts as a parent/wrapper for the child paths, which serve as the report types:
 *
 * - licensing
 * - sbom
 * - vulnerability
 *
 * Note that as part of this feature, report settings are moved to the project/release group settings page.
 */
export const ReportsV3Container: RouteComponent = ({ children, location }) => {
  const reportType = useReportType();

  // The URL pathname is always going to end with /reports/:reportType
  // This strips the trailing :reportType param so that we can use it as a base URL for the sidebar nav.
  const baseURL = stripLastPathSegment(location.pathname);

  // Permissions and feature flags.
  const isFullSecurityEnabled = useIsFeatureEnabled(FULL_SECURITY);
  const canDisplayVulnerabilityReport = useCanDisplaySecurityOptions();
  const isRemediationEnabled = useIsRemediationEnabled();

  // This helps us redirect if user tries to access a report type that they don't have access to.
  const shouldRedirect = useMemo(() => {
    // Everyone gets the licensing and sbom reports.
    const allowedReportTypes = new Set<ReportType>(['licensing', 'sbom']);

    if (canDisplayVulnerabilityReport) {
      allowedReportTypes.add('vulnerability');
    }

    if (isRemediationEnabled) {
      allowedReportTypes.add('remediationGuidance');
    }

    return !allowedReportTypes.has(reportType);
  }, [canDisplayVulnerabilityReport, isRemediationEnabled, reportType]);

  if (shouldRedirect) {
    return <Redirect to={`${baseURL}/licensing`} />;
  }

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>
          Reports
        </SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem to={`${baseURL}/licensing`} isActive={reportType === 'licensing'}>
          Licensing
        </SidebarNavLayout.NavItem>
        <SidebarNavLayout.NavItem to={`${baseURL}/sbom`} isActive={reportType === 'sbom'}>
          SBOM
        </SidebarNavLayout.NavItem>
        {canDisplayVulnerabilityReport && (
          <SidebarNavLayout.NavItem to={`${baseURL}/vulnerability`} isActive={reportType === 'vulnerability'}>
            Vulnerabilities
          </SidebarNavLayout.NavItem>
        )}
        {isRemediationEnabled && (
          <SidebarNavLayout.PaidNavItem
            to={`${baseURL}/remediationGuidance`}
            isActive={reportType === 'remediationGuidance'}
            isRestricted={!isFullSecurityEnabled}
          >
            Remediation Guidance
          </SidebarNavLayout.PaidNavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {children}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

/**
 * The actual page content for the Reports V3 page. This would be one of the child
 * components of the ReportsV3Container, ie: the licensing, sbom, or vulnerability report.
 */
export const ReportsV3Page: VoidFunctionComponent = () => {
  // TODO: we add a bit of padding here to match the mocks better, but it would good to fix this properly.
  // See: https://fossa.atlassian.net/browse/CORE-6467
  return (
    <main className="padding-left-sm">
      <Heading />
      <StepHeading step={1}>
        <ReportFlowSelector />
      </StepHeading>
      <StepHeading step={2}>
        <PreviewFiltersLayout>
          <PreviewFiltersLayout.Preview>
            <Preview />
          </PreviewFiltersLayout.Preview>
          <PreviewFiltersLayout.Filters>
            <Blurb />
            <div>
              {/* This <div> is needed for spacing/grouping reasons */}
              <FormatSelector />
              <GenerateReportButton />
            </div>
            <Divider />
            <div>
              Options
            </div>
          </PreviewFiltersLayout.Filters>
        </PreviewFiltersLayout>
      </StepHeading>
    </main>
  );
};
